'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Reads a `?error=<value>` URL param on mount, cleans the URL, and returns
 * whether the param was present. The check runs at most once per component
 * lifecycle (guarded by a ref).
 *
 * @param errorValue — the error string to match (e.g. `'SessionCleared'`)
 * @param precondition — optional gate; when provided, the check is deferred
 *   until it returns `true` (e.g. waiting for auth status to resolve).
 *   Returning `false` skips the check for that render; `true` runs it once.
 */
export function useUrlErrorParam(errorValue: string, precondition?: () => boolean): boolean {
  const [show, setShow] = useState(false);
  const checked = useRef(false);

  useEffect(() => {
    if (checked.current) return;
    if (precondition != null && !precondition()) return;
    checked.current = true;

    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('error') !== errorValue) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- one-time URL param initialization, not reacting to prop changes
    setShow(true);

    const url = new URL(globalThis.location.href);
    url.searchParams.delete('error');
    globalThis.history.replaceState({}, '', url.toString());
  }, [errorValue, precondition]);

  return show;
}
